import dayjs from 'dayjs';
import { Message } from 'element-ui';

/**
 * @description 格式化单个签收请求参数
 * @param {Object} receiptInfo 签收详情表单数据
 * @param {Array} receiptDetailSignDtoList // 签收明细列表
 * @param {String} operateType // 操作类别 01: 网批驳回，02: 签收确认, 03: 拒收
 */
export const formatSingleSignRequestParams = (receiptInfo, receiptDetailSignDtoList, operateType) => {
    const params = {};
    ['businessMode', 'customerOrderNo', 'dispatchNo', 'distributionFlag', 'distributionLastFlag', 'orderNo', 'orderType', 'signStatus', 'taskNo', 'version'].forEach(k => {
        params[k] = receiptInfo[k] || '';
    });
    return {
        batchSignFlag: '1', // 是否批量签收 0: 是批量 1：非批量
        operateType: operateType,
        receiptDetailSignDtoList,
        receiptSignDto: params,
        signContact: receiptInfo.signContact || receiptInfo.currentUserName, // 签收人
        signTime: receiptInfo.signTime || dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'), // 签收时间
        signType: receiptInfo.signType || '3', // 签收类型
        unloadCharge: receiptInfo.unloadCharge, // 卸货费标识
        upstairsCharge: receiptInfo.upstairsCharge // 上楼费标识
    };
};

/**
 * @description 计算正品数量 计算规则：正品数量 = 实发数量 - (污染 + 破损 + 短少 + 拒收)
 * realDeliverCount: 实发数量
 * mildewPolluteCount: 污染数量
 * packageDamagedCount: 破损数量
 * stockoutCount: 短少数量
 * rejectQty: 拒收数量
 */
export const computedRealReceiptCount = data => {
    const sumNum = sum([data.mildewPolluteCount, data.packageDamagedCount, data.stockoutCount, data.rejectQty]);
    return subtraction(data.realDeliverCount, sumNum);
};

/**
 * @description 校验签收确认提交条件
 * @param {Object} rowData 列表当前操作行数据
 * deliveredVerifyFlag: 签收类型：DELIVERED_VERIFY_FLAG
 * applyStatus：审批状态【N：新建、R：审核中、P：审批通过、C：作废】
 * goodsFeeStatus: 货款收款状态: OTP_TRANSPORT_FEE_STATUS
 * transportFeeStatus：运费收款状态：OTP_TRANSPORT_FEE_STATUS
 */
export const validateSignComfirm = rowData => {
    // 签收类型为电子签收且审核状态不是审核通过时，不让提交
    if (+rowData.deliveredVerifyFlag === 2 && rowData.applyStatus !== 'P') {
        return Message.warning('请到直通宝/美的通/收货短信做电子签收!');
    }
    // 未收款不允许签收
    if (+rowData.transportFeeStatus === 1) {
        return Message.warning('未收款订单需收款后才能进行签收!');
    }

    // 代收货款未完成，不允许签收
    if (+rowData.goodsFeeStatus === 1) {
        return Message.warning('订单的代收货款流程未完成，请联系司机完成代收货款再进行签收!');
    }
};

const decimalPlaces = 4;

const calcFlag = Math.pow(10, decimalPlaces);

/**
 * @description: 将传入的值，格式化为number类型
 * @param {String} x 需求格式化的值
 * @return {Number} 格式化后的值
 */
export const toNum = (x) => {
    return +x || 0;
};

/**
 * @description: 判断一个字符串是否是有效数字
 * @param {String} str 传入的字符串
 * @return {Boolean} true/false
 */
export const isNumeric = (str) => {
    return !isNaN(parseFloat(str)) && isFinite(str);
};

/**
 * @description: 将有效数字的字符串四舍五入，并保留{{ decimalPlaces }}位小数,小数位数不够时，不作补0处理
 * @param {String} num 传入的字符串
 * @return {String} 格式化后的值
 */
export const roundDecimalPlaces = (num) => {
    if (isNumeric(num)) {
        return Math.round(num * calcFlag) / calcFlag;
    } else {
        return num;
    }
};

/**
 * @description: 将传入的数组，每一项格式化为数字，并求和，解决浮点数加减失精问题
 * @param {Array} arr
 * @return {Number} 求和后的结果
 */
export const sum = (arr) => {
    return arr.reduce((acc, n) => acc + Math.round(toNum(n) * calcFlag), 0) / calcFlag;
};

/**
 * @description: 将传入的数值相减，a-b，解决浮点数加减失精问题
 * @param {Number} a
 * @param {Number} b
 * @return {Number} 求差的结果
 */
export const subtraction = (a, b) => {
    return (Math.round(a * calcFlag) - Math.round(b * calcFlag)) / calcFlag;
};

export const numInputLimit = (row, prop) => {
    let num = row[prop];
    if (row.cdcmManufacturer && row.cdcmManufacturer === 'Y') {
        // 移除非数字和小数点
        num = num.replace(/[^0-9.]/g, '');

        // 移除开头的多余的零
        num = num.replace(/^0+(?=\d)/, '');

        // 如果以 . 开头，补充 0
        if (num.startsWith('.')) {
            num = '0' + num;
        }

        // 检查是否包含多个小数点
        const decimalCount = (num.match(/\./g) || []).length;
        if (decimalCount > 1) {
            num = num.slice(0, num.lastIndexOf('.'));
        }

        // 检查小数位数
        const decimalPart = num.split('.')[1];
        if (decimalPart && decimalPart.length > decimalPlaces) {
            num = num.slice(0, num.lastIndexOf('.') + decimalPlaces + 1);
        }
    } else {
        // 移除非数字
        num = num.replace(/[^\d]+$/g, '');

        // 移除开头的多余的零
        num = num.replace(/^0+(?=\d)/, '');
    }
    return num;
};

/**
 * @description 针对 每一项都为对象的数组，校验两个数组中每一个对象的指定key，所对应的value是否一致
 * @param {Array} arrA 表单提交的数组
 * @param {Array} arrB 初始数组
 * @param {Array} signPushFieldList 指定key的字段集合 数组
 * @return {Boolean} 全部一致 返回true，反之false
 */
export const areArraysEqual = (arrA, arrB, signPushFieldList) => {
    return arrA.every((itemA, index) => {
        const itemB = arrB[index];
        return isObjectsEqual(itemA, itemB, signPushFieldList);
    });
};

/**
 * @description 校验两个对象指定key，所对应的value，是否一致
 * @param {Object} objA 表单提交时 数组中的每一项
 * @param {Object} objB 初始数组中的每一项
 * @param {Array} signPushFieldList 指定key的字段集合 数组
 * @return {Boolean} 全部一致 返回true，反之false
 */
export const isObjectsEqual = (objA, objB, fieldList) => {
    return fieldList.every(key => +objA[key] === objB[key]);
};

/**
 * @description 针对 每一项都为对象的数组，校验两个数组中每一个对象的指定key，所对应的value是否减少
 * @param {Array} arrA 表单提交的数组
 * @param {Array} arrB 初始数组
 * @param {String} signPushField 指定key的字段
 * @return {Boolean} 存在减少 返回true，反之false
 */
export const areArraysReduce = (arrA, arrB, signPushField) => {
    return arrA.some((itemA, index) => {
        const itemB = arrB[index];
        return +itemA[signPushField] < +itemB[signPushField];
    });
};

/**
 * @description 针对 每一项都为对象的数组，校验数组中每一个对象的指定key，所对应的value，是否不为负数
 * @param {Array} arr 待校验的数据，即 表单提交时的数据
 * @param {Array} signPushFieldList 指定key的字段集合 数组
 * @return {Boolean} 全部一致 返回true，反之false
 */
export const checkAllPositiveValues = (arr, fieldList) => {
    const allPositiveValues = obj => {
        return fieldList.map(key => obj[key]).every(val => +val >= 0);
    };
    return arr.every(allPositiveValues);
};

/**
 * @description 将一个对象映射为{label: 'value', value: 'key'}形式
 * @param {Object} object 需要映射的对象
 * @returns {Array} [{ label: 'value', value: 'key' }, { label: 'value', value: 'key' }]
 */
export const formatObjectToLabelValue = (object) => {
    return Object.entries(object).map(v => ({ label: v[1], value: v[0] }));
};

/**
 * @description: 针对 每一项都为对象的数组，过滤出指定key的值为value的项，如果 key或value传入空值，则返回原始数组
 * @param {Array} array 需要过滤的数组
 * @param {String} key 指定的key
 * @param {String} value 指定的value
 * @return {Array} 过滤后的数据
 */
export const filterArrayByValue = (array, key, value) => {
    if (!key || !value) {
        return array;
    }
    return array.filter(item => item[key] === value);
};

/**
 * @description: 针对 每一项都为对象的数组，过滤出指定key的值不为null、undefined、空字符串的项
 * @param {Array} array 需要过滤的数组
 * @param {String} key 指定的key
 * @return {Array} 过滤后的数据
 */
export const filterObjectsByProperty = (arr, property) => {
    return arr.filter(obj => obj.hasOwnProperty(property) && obj[property] !== null && obj[property] !== undefined && obj[property] !== '');
};

/**
 * @description: 针对 每一项都为对象的数组，将每一项过滤出指定key的键值对
 * @param {Array} array 需要过滤的数组
 * @param {Array} keysToKeep 指定的key
 * @return {Array} 过滤后的数据
 */
export const filterObjectsByKeys = (array, keysToKeep) => {
    return array.map(function (obj) {
        const newObj = {};
        keysToKeep.forEach(function (key) {
            if (obj.hasOwnProperty(key)) {
                newObj[key] = obj[key];
            }
        });
        return newObj;
    });
};

/**
 * @description: 针对 每一项都为对象的数组，将每一项值为null的转换为数字0（改变原数组）
 * @param {*} array 需要重构的数组
 * @return {*} 重构后的数组
 */
export const convertNullToZeroInPlace = (array) => {
    array.forEach(obj => {
        Object.keys(obj).forEach(key => {
            if (obj[key] === null) {
                obj[key] = 0;
            }
        });
    });
    return array;
};

/**
 * @description: 重组参数 将 {key1: value1,value2, key2: value3,value4} 转换为 [{key1: value1, key2: value3}, {key1: value2, key2: value4}]
 * @param {*} data 需要重组的数据
 * @return {*}
 */
export const convertToObject = (data) => {
    const maxLength = Math.max(...Object.values(data).map((value) => value.split(',').length));
    return Array.from({ length: maxLength }, (_, i) => Object.fromEntries(Object.entries(data).map(([key, value]) => {
        const valueArray = value.split(',');
        return [key, valueArray[i] ? valueArray[i].trim() : ''];
    })));
};

/**
 * @description 回单签收表格字段是否可编辑。默认(正品、污染、破损、短少、拒收、备注)等字段不可编辑
 * 02: 签收
 * 03: 拒收
 * 100: 重推
 * 101: 回单详情
 */
export const tableFieldIsEdit = {
    '02': {
        realReceive: false,
        mildewPolluteCount: true,
        packageDamagedCount: true,
        stockoutCount: true,
        rejectQty: true,
        signRemark: true
    },
    '03': {
        realReceive: false,
        mildewPolluteCount: false,
        packageDamagedCount: false,
        stockoutCount: false,
        rejectQty: false,
        signRemark: true
    },
    '100': {
        realReceive: true,
        mildewPolluteCount: false,
        packageDamagedCount: false,
        stockoutCount: false,
        rejectQty: true,
        signRemark: false
    },
    '101': {
        realReceive: false,
        mildewPolluteCount: false,
        packageDamagedCount: false,
        stockoutCount: false,
        rejectQty: false,
        signRemark: false
    }
};

/**
 * @description 回单签收表格字段是否可编辑。默认都不可编辑
 * 02: 签收
 * 03: 拒收
 * 100: 重推
 * 101: 回单详情
 */
export const B2BTableFieldIsEdit = {
    '02': {
        goodSignQty: false,
        machinedmgSignQty: true,
        packingdmgSignQty: true,
        fittingshortSignQty: true,
        goodRejectQty: true,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        fittingshortRejectQty: true,
        performRejectQty: true,
        shortQty: true,
        signRemark: true
    },
    '03': {
        goodSignQty: false,
        machinedmgSignQty: false,
        packingdmgSignQty: false,
        fittingshortSignQty: false,
        goodRejectQty: false,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        fittingshortRejectQty: true,
        performRejectQty: true,
        shortQty: false,
        signRemark: true
    },
    '100': {
        goodSignQty: true,
        machinedmgSignQty: true,
        packingdmgSignQty: true,
        fittingshortSignQty: true,
        goodRejectQty: true,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        fittingshortRejectQty: true,
        performRejectQty: true,
        shortQty: false,
        signRemark: false
    },
    '101': {
        goodSignQty: false,
        machinedmgSignQty: false,
        packingdmgSignQty: false,
        fittingshortSignQty: false,
        goodRejectQty: false,
        packingdmgRejectQty: false,
        machinedmgRejectQty: false,
        fittingshortRejectQty: false,
        performRejectQty: false,
        shortQty: false,
        signRemark: false
    }
};

/**
 * @description 回单签收表格字段是否可编辑。默认都不可编辑
 * 02: 签收
 * 03: 拒收
 * 100: 重推
 * 101: 回单详情
 */
export const B2CTableFieldIsEdit = {
    '02': {
        goodSignQty: false,
        packingdmgSignQty: true,
        goodRejectQty: true,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        shortQty: true,
        signRemark: true
    },
    '03': {
        goodSignQty: false,
        packingdmgSignQty: false,
        goodRejectQty: false,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        shortQty: false,
        signRemark: true
    },
    '100': {
        goodSignQty: true,
        packingdmgSignQty: true,
        goodRejectQty: true,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        shortQty: true,
        signRemark: false
    },
    '101': {
        goodSignQty: false,
        packingdmgSignQty: false,
        goodRejectQty: false,
        packingdmgRejectQty: false,
        machinedmgRejectQty: false,
        shortQty: false,
        signRemark: false
    }
};

/**
 * @description 菜鸟-回单签收表格字段是否可编辑。默认都不可编辑
 * 02: 签收
 * 03: 拒收
 * 101: 回单详情
 */
export const CNTableFieldIsEdit = {
    '02': {
        goodSignQty: true,
        packingdmgSignQty: true,
        machinedmgSignQty: true,
        goodRejectQty: true,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        fittingshortRejectQty: true,
        performRejectQty: true,
        shortQty: true,
        signRemark: true
    },
    '03': {
        goodSignQty: true,
        packingdmgSignQty: true,
        machinedmgSignQty: true,
        goodRejectQty: true,
        packingdmgRejectQty: true,
        machinedmgRejectQty: true,
        fittingshortRejectQty: true,
        performRejectQty: true,
        shortQty: true,
        signRemark: true
    },
    '101': {
        goodSignQty: false,
        packingdmgSignQty: false,
        machinedmgSignQty: false,
        goodRejectQty: false,
        packingdmgRejectQty: false,
        machinedmgRejectQty: false,
        fittingshortRejectQty: false,
        performRejectQty: false,
        shortQty: false,
        signRemark: false
    }
};
